<template>
  <form v-if="!userStatus" @submit.prevent="submit">
    <Stack>
      <FormTextInput
        :error="formattedErrors.email"
        :value="email"
        label="Email address"
        name="email"
        type="email"
        autocomplete="username"
        @update:modelValue="updateEmailValue"
      />
      <div class="flex justify-end">
        <Button :isLoading="isLoading" type="submit" text="Check email" />
      </div>
    </Stack>
  </form>

  <div v-else>
    <Stack v-if="userStatus === 'SSO'" class="w-full">
      <Box
        v-if="automaticallyRedirectToSsoProvider"
        class="w-full"
        background-color="background-light"
        border-radius="medium"
        padding="gutter"
        padding-y="large"
      >
        <Stack align="center">
          <Spinner size="xlarge" />
          <Typography variant="label">Logging you in...</Typography>
        </Stack>
      </Box>
      <Button
        v-else
        class="w-full"
        size="large"
        :link="ssoProvider?.login_url"
        :text="`Log in with ${ssoProvider.name}`"
      />
    </Stack>

    <ActiveForm
      v-else-if="userStatus === 'ACTIVE'"
      :email="email"
      :loginUrl="loginUrl"
      :forgotPasswordUrl="forgotPasswordUrl"
      :hasAdminAccount="isCustomerLogin && hasAdminAccount"
    />

    <InvitedForm
      v-else-if="userStatus === 'INACTIVE'"
      :email="email"
      :loginViaVerificationCodeUrl="loginViaVerificationCodeUrl"
    />

    <Stack space="small" v-else>
      <slot name="new-user-message">
        <Typography as="p">It looks like you don't have an account yet!</Typography>
        <Typography as="p">
          Head to the
          <a
            class="font-medium hover:underline text-primary"
            :href="route('web.company.registration.show')"
          >
            registration page
          </a>
          to get started, or
          <a href="#" @click="handleResetForm" class="font-medium hover:underline text-primary">
            try another email address </a
          >.
        </Typography>
      </slot>
    </Stack>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Button, FormTextInput, Stack, Typography, Spinner, Box } from '@aryeo/runway'
import { router, usePage } from '@inertiajs/vue3'

import { useTenant } from '@/Hooks/Shared/useTenant'
import { useApiErrors } from '@/Hooks/Shared/useApiErrors'
import LoginApi from '@/Services/Api/Company/V1/LoginApi'

import ActiveForm from '@/Components/Shared/Forms/ActiveForm.vue'
import InvitedForm from '@/Components/Shared/Forms/InvitedForm.vue'

const props = defineProps({
  isCustomerLogin: {
    type: Boolean,
    default: false,
  },
  loginUrl: {
    type: String,
    required: true,
  },
  ssoProvider: Object,
  automaticallyRedirectToSsoProvider: {
    type: Boolean,
    default: true,
  },
  loginViaVerificationCodeUrl: {
    type: String,
    required: true,
  },
  forgotPasswordUrl: {
    type: String,
    required: true,
  },
  registerUrl: {
    type: String,
    default: null,
  },
  email: {
    type: String,
    default: null,
  },
})

const { tenant } = useTenant()

const { formattedErrors, setErrors, clearErrors } = useApiErrors()

const page = usePage()

const email = ref(props.email)
const isLoading = ref(false)
const userStatus = ref(page.props?.loginState ?? null)
const hasAdminAccount = ref(false)

function updateEmailValue({ value }) {
  email.value = value.toLowerCase()
}

function handleResetForm() {
  email.value = null
  userStatus.value = null
  clearErrors()
}

function submit() {
  isLoading.value = true

  LoginApi.checkAuthEmail(email.value, tenant.value?.company.id)
    .then((res) => {
      if (props.registerUrl && res.data.status === 'NEW') {
        router.visit(props.registerUrl)
        return
      }

      userStatus.value = res.data.status
      hasAdminAccount.value = res.data.has_admin_account ?? false

      if (props.automaticallyRedirectToSsoProvider && userStatus.value === 'SSO') {
        window.location.href = props.ssoProvider?.login_url
      }
    })
    .catch((err) => {
      setErrors(err.response.data?.data)
    })
    .finally(() => {
      isLoading.value = false
    })
}
</script>
